import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { coloreStato } from "../../utils/statoColors";

/** Grigio per stati non mappati (es. gli interventi sincronizzati da Odoo, che
 *  oggi arrivano senza `stato`) e per "Senza stato". */
const FALLBACK = "#6b7280";

/**
 * Palette per gli stati che restano fuori da STATO_HEX anche dopo la
 * normalizzazione. Assegnata in ordine di apparizione.
 *
 * Il primo colore NON è più #2563eb: quel blu è passato a "Programmata" (vedi
 * statoColors.ts), e uno stato sconosciuto capitato all'indice 0 avrebbe preso
 * lo stesso identico colore dello stato programmato nella stessa torta.
 */
const PALETTE_EXTRA = ["#8b5cf6", "#0891b2", "#c2410c", "#be123c", "#4d7c0f"];

export function coloreFetta(stato: string, indice: number): string {
  if (stato === "Senza stato") return FALLBACK;
  return coloreStato(stato) ?? PALETTE_EXTRA[indice % PALETTE_EXTRA.length] ?? FALLBACK;
}

interface DonutPerStatoProps {
  titolo: string;
  perStato: Record<string, number>;
  isLoading?: boolean;
  /** Ordine preferito degli stati (es. STATO_ORDER); gli altri seguono per conteggio. */
  ordine?: readonly string[];
  /**
   * Se presente, fetta e voce di legenda diventano cliccabili e invocano
   * questa callback con lo stato scelto (drill-down verso la lista filtrata).
   * Omessa, il donut resta puramente informativo.
   */
  onSelezionaStato?: (stato: string) => void;
}

/**
 * Donut della distribuzione per stato, generico su qualunque modulo: gli stati
 * arrivano come mappa { stato: n } dal backend, non da un elenco fisso, perché
 * ogni modulo ha la sua enumerazione (e gli Interventi ricevono valori liberi
 * da Odoo).
 *
 * Regge anche il caso limite reale di oggi — 4 interventi tutti "Senza stato":
 * mostra un anello a un solo segmento con la legenda, che è l'informazione
 * onesta, invece di sparire o sembrare rotto.
 */
export function DonutPerStato({ titolo, perStato, isLoading, ordine, onSelezionaStato }: DonutPerStatoProps) {
  const voci = Object.entries(perStato)
    .filter(([, n]) => n > 0)
    .sort((a, b) => {
      if (ordine) {
        const ia = ordine.indexOf(a[0]);
        const ib = ordine.indexOf(b[0]);
        if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      }
      return b[1] - a[1];
    })
    .map(([stato, value], i) => ({ stato, value, fill: coloreFetta(stato, i) }));

  const totale = voci.reduce((s, v) => s + v.value, 0);

  return (
    <div className="rounded-card border border-line bg-white p-4 shadow-card">
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink/50">{titolo}</p>

      {isLoading ? (
        <p className="text-sm text-ink/40">Caricamento…</p>
      ) : totale === 0 ? (
        <p className="py-6 text-center text-sm text-ink/40">Nessun record da mostrare.</p>
      ) : (
        <div className="flex items-center gap-3">
          <div className="h-28 w-28 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={voci}
                  dataKey="value"
                  nameKey="stato"
                  innerRadius={34}
                  outerRadius={54}
                  // Caso limite reale (Interventi: 4 record tutti "Senza
                  // stato"): con un solo stato il settore copre l'intero giro.
                  // Vanno azzerati padding e cornerRadius E disattivata
                  // l'animazione di ingresso: quest'ultima parte da ampiezza
                  // zero e sul settore da 360° non progredisce, lasciando
                  // startAngle === endAngle — recharts allora non emette
                  // nessun path e l'anello resta invisibile.
                  paddingAngle={voci.length > 1 ? 3 : 0}
                  cornerRadius={voci.length > 1 ? 3 : 0}
                  isAnimationActive={voci.length > 1}
                  onClick={onSelezionaStato ? (v: { stato: string }) => onSelezionaStato(v.stato) : undefined}
                >
                  {voci.map((v) => (
                    <Cell key={v.stato} fill={v.fill} cursor={onSelezionaStato ? "pointer" : undefined} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {/* La legenda è cliccabile quanto le fette: con conteggi piccoli la
              fetta è un bersaglio minuscolo, il testo no — ed è anche l'unico
              punto raggiungibile da tastiera. */}
          <ul className="flex min-w-0 flex-1 flex-col gap-1.5">
            {voci.map((v) => {
              const riga = (
                <>
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: v.fill }} />
                  <span className="truncate" title={v.stato}>
                    {v.stato}
                  </span>
                  <span className="ml-auto shrink-0 font-semibold tabular-nums text-ink">{v.value}</span>
                </>
              );
              return (
                <li key={v.stato} className="min-w-0">
                  {onSelezionaStato ? (
                    <button
                      type="button"
                      onClick={() => onSelezionaStato(v.stato)}
                      title={`Mostra i record con stato "${v.stato}"`}
                      className="-mx-1 flex w-full min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-left text-xs text-ink/70 transition-colors hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                    >
                      {riga}
                    </button>
                  ) : (
                    <span className="flex min-w-0 items-center gap-1.5 text-xs text-ink/70">{riga}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
